import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RelatedPartyPaymentService } from '../../services/related-party-payment.service';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-related-party-payment-allocation',
    templateUrl: './related-party-payment-allocation.component.html',
    styleUrls: ['./related-party-payment-allocation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RelatedPartyPaymentAllocationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // Standard component properties (MANDATORY)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  loadingAccounts = false;

  // Component data
  rowData: any = {};
  gridData: any[] = [];
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  
  // Modal title
  modalTitle = "Payor Allocation";
  
  // Business logic properties from AngularJS
  is_allocate_access = false;
  extPayorData: any[] = [];
  allocatedPayors: any[] = [];
  tp_pool_list: any[] = [];
  tp_dropdown_data: any[] = [];
  tp_me_dropdown_data: any[] = [];
  payorAccountData: any[] = [];
  entityData: any[] = [];
  entityOriginalData: any[] = [];
  le_key_list: any[] = [];
  M3payorList: any[] = [];
  clob_settings: any[] = [];
  cc_acct_clob: any[] = [];
  le_issue_clob: any[] = [];
  
  screen_list = [
    {"KEY": 10818, "LABEL": "Related Party Payments Reconciliation", "TYPE": "Reconciliation"}, 
    {"KEY": 10805, "LABEL": "Related Party Payments - Sys Accts", "TYPE": "SYS"},
    {"KEY": 10800, "LABEL": "Related Party Payments - Sys & Local Accts", "TYPE": "LOCAL"},
    {"KEY": 10834, "LABEL": "M3 Payor Reconciliation Grid", "TYPE": "M3Payor"}
  ];
  
  active_screen_key: any = '';
  source_screen_type = '';
  userSelectedData: any[] = [];
  globalScenarioDesc = '';
  payment_type = '';
  classification_type = '';
  trans_type = '';
  le_keys = '';
  taxYear: any;
  scenario: any;
  glbl_issue_id: any;
  logged_in_user = '';
  
  M3_Payor_types = [
    {"PAYOR_TYPE": 'Foreign Corp', "TYPE": 'FC'},
    {"PAYOR_TYPE": 'Domestic Corp', "TYPE": 'DC'},	
    {"PAYOR_TYPE": 'Domestic Partnership', "TYPE": 'DP'},
    {"PAYOR_TYPE": 'Foreign Partnership', "TYPE": 'FP'}
  ];

  // Typeahead/Autocomplete properties
  loadingTPs = false;
  noResults = false;
  searchTerm = '';
  
  // ✅ Autocomplete: Map to store filtered options for each row
  filteredTradingPartners: { [key: string]: Observable<any[]> } = {};
  
  // ✅ Autocomplete: Map to store search subjects for each row
  private searchSubjects: { [key: string]: Subject<string> } = {};

  constructor(
    private service: RelatedPartyPaymentService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    console.log('RelatedPartyPaymentAllocationComponent initialized');
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    const rawGrid = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(rawGrid) ? rawGrid : [];
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
    };
    this.cdr.markForCheck();
  }

  // V9: Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.gridData || this.gridData.length === 0) {
      return;
    }
    
    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      console.log("=======relatedPartyPaymentData=======");
      console.log("gridData:", this.gridData);
      console.log("rowData:", this.rowData);
      console.log("filterParams:", this.filterParams);
      
      // ✅ Set base URL from GENERAL_CONFIG (must include /gtw/)
      const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
      if (baseUrl) {
        this.service.setBaseUrl(baseUrl);
        console.log('✅ RPP Allocation: Base URL set successfully:', baseUrl);
      } else {
        console.error('❌ RPP Allocation: Base URL not found in GENERAL_CONFIG!');
      }
      
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
      const gridFilters: any = { ...normalizeFilterParams(activeScreen) };
      console.log('📋 gridFilters from workspace:', gridFilters);
      if (activeScreen.dataCurFilters) {
        this.globalScenarioDesc = activeScreen.dataCurFilters[1]?.valueLabel || '';
        console.log('📋 globalScenarioDesc from dataCurFilters:', this.globalScenarioDesc);
      }
      
      // Initialize properties from data
      this.userSelectedData = this.gridData;
      
      // ✅ Use gridFilters for payment_type and classification_type (NOT filterParams!)
      this.payment_type = gridFilters.category_filter || '';
      this.classification_type = gridFilters.category_filter || '';
      
      // ✅ Merge gridFilters into filterParams for later use
      this.filterParams = {
        ...this.filterParams,
        ...gridFilters,
        globalScenarioDesc: this.globalScenarioDesc
      };
      
      if (this.userSelectedData.length > 0 && this.userSelectedData[0].data) {
        this.trans_type = this.userSelectedData[0].data.TRANS_TYPE;
      }
      
      this.taxYear = this.globalService.globalParams?.tax_year;
      this.scenario = this.globalService.globalParams?.scenario;
      this.glbl_issue_id = this.globalService.globalParams?.issue_id;
      this.logged_in_user = this.globalService.userSettingsData?.user?.sso_id || '';
      
      // Get active screen key from globalParams
      this.active_screen_key = this.globalService.globalParams?.screen_key;
      
      console.log("payment_type:", this.payment_type);
      console.log("globalScenarioDesc:", this.globalScenarioDesc);
      console.log("trans_type:", this.trans_type);
      
      // Load payor details
      if (this.userSelectedData.length != 0) {
        this.getPayorDetails();
      }
      
      // Load external payor list for Interest
      if (this.payment_type == "INTEREST") {
        this.getExtPayorList();
      }
      
      this.showFormLoader = false;
      this.cdr.detectChanges();
      
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if scenario can be edited
   */
  checkEditScenario(s_type_code: string, t_scenario: any, t_issue: any): boolean {
    if (this.scenario == t_scenario && ((s_type_code == 'EXAM' && this.glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  /**
   * Get payor details based on screen type
   */
  getPayorDetails() {
    this.loadingAccounts = true;
    this.setSelectedParams();
    
    if (this.source_screen_type === "SYS" || this.source_screen_type === "LOCAL") {
      this.getPayorModalDataByAcct();
    } else {
      this.getPayorAccountData();
    }
  }

  /**
   * Set selected params based on screen type
   */
  setSelectedParams() {
    console.log("-----In setSelectedParams fn----");
    
    const screen_index = this.screen_list.findIndex(x => x.KEY == this.active_screen_key);
    this.source_screen_type = (screen_index >= 0) ? this.screen_list[screen_index].TYPE : '';
    
    console.log("active_screen_key:", this.active_screen_key);
    console.log("screen_index:", screen_index);
    console.log("source_screen_type:", this.source_screen_type);
    
    if (this.source_screen_type != '') {
      if (this.source_screen_type === "SYS") {
        for (let i = 0; i < this.userSelectedData.length; i++) {
          this.cc_acct_clob.push({
            "COMBINATION_KEY": this.userSelectedData[i].data.COMBINATION_KEY,
            "ACCT_KEY": this.userSelectedData[i].data.ACCT_KEY,
            "ACCT": this.userSelectedData[i].data.ACCT,
            "ISSUE_KEY": this.userSelectedData[i].data.ISSUE_KEY
          });
        }
      } else if (this.source_screen_type === "LOCAL") {
        for (let i = 0; i < this.userSelectedData.length; i++) {
          this.cc_acct_clob.push({
            "COMBINATION_KEY": this.userSelectedData[i].data.COMBINATION_KEY,
            "ACCT_KEY": this.userSelectedData[i].data.FROM_ACCT_KEY,
            "ACCT": this.userSelectedData[i].data.FROM_ACCT,
            "ISSUE_KEY": this.userSelectedData[i].data.ISSUE_KEY
          });
        }
      } else {
        for (let i = 0; i < this.userSelectedData.length; i++) {
          if (this.payment_type === "M3PAYOR") {
            this.le_key_list.push(this.userSelectedData[i].data.COMBINATION_KEY);
          } else {
            this.le_issue_clob.push({
              "LE_KEY": this.userSelectedData[i].data.LE_KEY,
              "ISSUE_KEY": this.userSelectedData[i].data.ISSUE_KEY
            });
          }
        }
        this.le_keys = this.le_key_list.toString();
      }
    }
    
    console.log("userSelectedData:", this.userSelectedData);
    console.log("CC Clob:", this.cc_acct_clob);
    console.log("LE Issue Clob:", this.le_issue_clob);
  }

  /**
   * Get payor account data (for Reconciliation screen type)
   */
  getPayorAccountData() {
    this.service.getPayorAccountDetails(
      this.le_issue_clob,
      this.payment_type,
      this.classification_type,
      this.trans_type,
      this.logged_in_user,
      this.globalService.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("::::::getPayorAccountData()::::");
        console.log("Payor Account Details:", data);
        
        this.loadingAccounts = false;
        this.payorAccountData = data.jsonObject;
        this.setPayorModalData();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error loading payor account data:", error);
        this.loadingAccounts = false;
        this.showError(error.message || 'Error loading payor account data');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Get payor modal data by account (for SYS/LOCAL screen types)
   */
  getPayorModalDataByAcct() {
    this.service.getPayorModalByAcct(
      this.cc_acct_clob,
      this.source_screen_type,
      this.payment_type,
      this.classification_type,
      this.trans_type,
      this.logged_in_user,
      this.globalService.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("::::::getPayorAccountDataByAcct()::::");
        console.log("data.jsonObject:", data.jsonObject);
        
        this.loadingAccounts = false;
        this.payorAccountData = data.jsonObject;
        this.setPayorModalData();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error loading payor modal data by acct:", error);
        this.loadingAccounts = false;
        this.showError(error.message || 'Error loading payor data');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Get external payor list (for Interest payment type)
   */
  getExtPayorList() {
    this.service.getExtPayorDetails(this.globalService.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("External Payors Details:", data);
        console.log("External Payors Details json Object:", data.jsonObject);
        
        this.extPayorData = data.jsonObject;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error loading external payor list:", error);
        this.showError(error.message || 'Error loading external payor list');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Get M3 Payor List
   */
  getM3PayorList() {
    this.service.getM3PayorList(this.globalService.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("::::::getM3PayorList()::::");
        console.log("M3 Payor List JSON:", data);
        console.log("M3 Payor List was data.jsonObject:", data.jsonObject);
        
        this.M3payorList = data.jsonObject;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error loading M3 payor list:", error);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Set trading partner dropdown list
   */
  setTradingPartnerDropdownList() {
    for (let i = 0; i < this.payorAccountData.length; i++) {
      const dup_index = this.tp_pool_list.findIndex(x => 
        x.TP_LEID === this.payorAccountData[i].LEID && 
        x.TP_CDR_NO === this.payorAccountData[i].CDR_NO &&
        x.TP_REP_PD === this.payorAccountData[i].REPORTING_PERIOD &&
        x.TP_ME_CODE === this.payorAccountData[i].ME_CODE
      );
      
      if (dup_index < 0) {
        this.tp_pool_list.push({
          "TP_LEID": this.payorAccountData[i].LEID,
          "TP_CDR_NO": this.payorAccountData[i].CDR_NO,
          "TP_REP_PD": this.payorAccountData[i].REPORTING_PERIOD,
          "TP_ME_CODE": this.payorAccountData[i].ME_CODE,
          "TP_ENTITY": this.payorAccountData[i].LEID + "-" + 
                       this.payorAccountData[i].CDR_NO + "-" + 
                       this.payorAccountData[i].REPORTING_PERIOD + "-" + 
                       this.payorAccountData[i].ME_CODE
        });
        
        if (this.payment_type.toUpperCase() !== "INTEREST") {
          this.tp_pool_list.push({
            "TP_LEID": this.payorAccountData[i].LEID,
            "TP_CDR_NO": this.payorAccountData[i].CDR_NO,
            "TP_REP_PD": this.payorAccountData[i].REPORTING_PERIOD,
            "TP_ME_CODE": this.payorAccountData[i].ME_CODE,
            "TP_ENTITY": this.payorAccountData[i].LEID + "-" + 
                         this.payorAccountData[i].CDR_NO + "-" + 
                         this.payorAccountData[i].REPORTING_PERIOD
          });
        }
      }
    }
    
    console.log(":::setTradingPartnerDropdownList:::");
    console.log(":::tp_pool_list:::", this.tp_pool_list);
  }

  /**
   * Set payor modal data - transform API data into display structure
   */
  setPayorModalData() {
    console.log("---------setPayorModalData()---------");
    console.log("📦 RAW payorAccountData from API:", this.payorAccountData);
    
    // Check if first item has BOOK_AMT, TOTAL_ALLOCATED_AMT, UNALLOCATED_AMT
    if (this.payorAccountData.length > 0) {
      console.log("📊 First item structure:", {
        FROM_ACCT: this.payorAccountData[0].FROM_ACCT,
        FROM_ACCT_KEY: this.payorAccountData[0].FROM_ACCT_KEY,
        BOOK_AMT: this.payorAccountData[0].BOOK_AMT,
        TOTAL_ALLOCATED_AMT: this.payorAccountData[0].TOTAL_ALLOCATED_AMT,
        UNALLOCATED_AMT: this.payorAccountData[0].UNALLOCATED_AMT,
        ALLOCATED_AMT: this.payorAccountData[0].ALLOCATED_AMT
      });
      
      // Show all items in table format
      console.table(this.payorAccountData.map((item: any) => ({
        FROM_ACCT: item.FROM_ACCT,
        FROM_ACCT_KEY: item.FROM_ACCT_KEY,
        BOOK_AMT: item.BOOK_AMT,
        TOTAL_ALLOCATED_AMT: item.TOTAL_ALLOCATED_AMT,
        UNALLOCATED_AMT: item.UNALLOCATED_AMT,
        ALLOCATED_AMT: item.ALLOCATED_AMT,
        ROW_TYPE: item.ROW_TYPE
      })));
    }
    
    // Call to set TP dropdown values including ME code
    this.setTradingPartnerDropdownList();
    
    // Group by CC_KEY
    const grouped: any = {};
    this.payorAccountData.forEach((item: any) => {
      if (!grouped[item.CC_KEY]) {
        grouped[item.CC_KEY] = [];
      }
      grouped[item.CC_KEY].push(item);
    });
    
    console.log("object grouped:", grouped);
    
    // Transform grouped data
    const entityArray = Object.keys(grouped).map((key: string) => {
      const items = grouped[key];
      const firstItem = items[0];
      
      if (firstItem.IS_ALLOCATION_ACCESS === "Y") {
        this.is_allocate_access = true;
      }
      
      return {
        CC_KEY: firstItem.CC_KEY,
        LEID: firstItem.LEID,
        CDR_NO: firstItem.CDR_NO,
        REP_PD: firstItem.REPORTING_PERIOD,
        ME_CODE: firstItem.ME_CODE,
        TP_ME_CODE: firstItem.TP_ME_CODE || "",
        TOTAL_ALLOCATED_AMT: 0,
        UNALLOCATED_AMT: 0,
        LEDGER: firstItem.LEDGER,
        SOURCE_SYSTEM_FLAG: firstItem.SOURCE_SYSTEM_FLAG,
        URP_FLAG: firstItem.URP_FLAG,
        ROW_TYPE: firstItem.ROW_TYPE,
        ISSUE_ID: firstItem.ISSUE_ID || "",
        T_SCENARIO_TYPE_CODE: firstItem.T_SCENARIO_TYPE_CODE,
        T_SCENARIO: firstItem.T_SCENARIO,
        Account_data: items
      };
    });
    
    console.log("object entityArray:", entityArray);
    this.entityData = entityArray;
    
      // Further process entityData for BOOK_AMT trans_type
      for (let i = 0; i < this.entityData.length; i++) {
        if (this.entityData[i].Account_data.length > 0 && this.entityData[i].Account_data[0].FROM_ACCT_KEY != null) {
          
          console.log(`🔍 Processing entity ${i} Account_data BEFORE grouping:`, this.entityData[i].Account_data);
          
          // Group by FROM_ACCT_KEY
          const acctGrouped: any = {};
          this.entityData[i].Account_data.forEach((acct: any) => {
            if (!acctGrouped[acct.FROM_ACCT_KEY]) {
              acctGrouped[acct.FROM_ACCT_KEY] = [];
            }
            acctGrouped[acct.FROM_ACCT_KEY].push(acct);
          });
          
          console.log("object acctGrouped for entity:", i, acctGrouped);
        
        // Transform account grouped data
        const accountArray = Object.keys(acctGrouped).map((key: string) => {
          const acctItems = acctGrouped[key];
          
          // ✅ Make a DEEP copy of the array (like AngularJS angular.copy)
          const acctItemsCopy = JSON.parse(JSON.stringify(acctItems));
          
          // ✅ Set first item as HEADER_ROW
          acctItemsCopy[0].ROW_TYPE = "HEADER_ROW";
          
          console.log(`🔧 BEFORE unshift - acctItems for FROM_ACCT_KEY ${key}:`, acctItems.map((x: any) => ({
            ROW_TYPE: x.ROW_TYPE,
            FROM_ACCT: x.FROM_ACCT,
            BOOK_AMT: x.BOOK_AMT,
            TOTAL_ALLOCATED_AMT: x.TOTAL_ALLOCATED_AMT,
            UNALLOCATED_AMT: x.UNALLOCATED_AMT
          })));
          
          // ✅ Add HEADER_ROW to beginning of original array for BOOK_AMT trans_type
          if (this.trans_type === "BOOK_AMT") {
            acctItems.unshift(acctItemsCopy[0]);
          }
          
          console.log(`🔧 AFTER unshift - acctItems[0] (HEADER_ROW) for FROM_ACCT_KEY ${key}:`, {
            ROW_TYPE: acctItems[0].ROW_TYPE,
            FROM_ACCT: acctItems[0].FROM_ACCT,
            BOOK_AMT: acctItems[0].BOOK_AMT,
            TOTAL_ALLOCATED_AMT: acctItems[0].TOTAL_ALLOCATED_AMT,
            UNALLOCATED_AMT: acctItems[0].UNALLOCATED_AMT,
            ALLOCATED_AMT: acctItems[0].ALLOCATED_AMT
          });
          
          console.log(`📊 Account ${key} - HEADER_ROW data:`, {
            FROM_ACCT: acctItems[0].FROM_ACCT,
            BOOK_AMT: acctItems[0].BOOK_AMT,
            TOTAL_ALLOCATED_AMT: acctItems[0].TOTAL_ALLOCATED_AMT,
            UNALLOCATED_AMT: acctItems[0].UNALLOCATED_AMT,
            ROW_TYPE: acctItems[0].ROW_TYPE
          });
          
          // ✅ Now acctItems[0] is the HEADER_ROW (for BOOK_AMT) or first item (for M_ADJUSTMENT)
          return {
            ROW_TYPE: acctItems[0].ROW_TYPE,
            LEID: acctItems[0].LEID,
            CDR_NO: acctItems[0].CDR_NO,
            REP_PD: acctItems[0].REPORTING_PERIOD,
            ME_CODE: acctItems[0].ME_CODE,
            FROM_ACCT: acctItems[0].FROM_ACCT,
            FROM_ACCT_DESC: acctItems[0].FROM_ACCT_DESC,
            FROM_ACCT_KEY: acctItems[0].FROM_ACCT_KEY,
            SCHDM_TEMPLATE_KEY: acctItems[0].SCHDM_TEMPLATE_KEY,
            ADJ_ORIGIN_SOURCE: acctItems[0].ADJ_ORIGIN_SOURCE,
            BOOK_AMT: acctItems[0].BOOK_AMT,
            TOTAL_ALLOCATED_AMT: acctItems[0].TOTAL_ALLOCATED_AMT,
            UNALLOCATED_AMT: acctItems[0].UNALLOCATED_AMT,
            T_SCENARIO: acctItems[0].T_SCENARIO,
            T_SCENARIO_TYPE_CODE: acctItems[0].T_SCENARIO_TYPE_CODE,
            ISSUE_ID: acctItems[0].ISSUE_ID,
            tp_dropdown_data: [],
            tpObject: {"TP_LEID":"", "TP_CDR_NO": "", "TP_REP_PD": 0, "TP_ME_CODE":"", "TP_ENTITY":""},
            tp_me_dropdown_data: [],
            TP_ME_CODE: acctItems[0].TP_ME_CODE,
            sub_acct_data: acctItems,
            DELETE_ROWS: []
          };
        });
        
        console.log("Account array for entity", i, ":", accountArray);
        this.entityData[i].Account_data = accountArray;
      }
    }
    
    console.log("entityData:", this.entityData);
    
    // ✅ Debug: Show structure of first entity's Account_data
    if (this.entityData.length > 0 && this.entityData[0].Account_data && this.entityData[0].Account_data.length > 0) {
      console.log("🔍 entityData[0].Account_data[0]:", this.entityData[0].Account_data[0]);
      console.log("🔍 entityData[0].Account_data[0].sub_acct_data:", this.entityData[0].Account_data[0].sub_acct_data);
      
      if (this.entityData[0].Account_data[0].sub_acct_data && this.entityData[0].Account_data[0].sub_acct_data.length > 0) {
        console.log("🔍 entityData[0].Account_data[0].sub_acct_data[0] (HEADER_ROW):", {
          ROW_TYPE: this.entityData[0].Account_data[0].sub_acct_data[0].ROW_TYPE,
          FROM_ACCT: this.entityData[0].Account_data[0].sub_acct_data[0].FROM_ACCT,
          BOOK_AMT: this.entityData[0].Account_data[0].sub_acct_data[0].BOOK_AMT,
          TOTAL_ALLOCATED_AMT: this.entityData[0].Account_data[0].sub_acct_data[0].TOTAL_ALLOCATED_AMT,
          UNALLOCATED_AMT: this.entityData[0].Account_data[0].sub_acct_data[0].UNALLOCATED_AMT
        });
      }
    }
    
    // Load TP dropdown list for Tax Adjustments
    if (this.trans_type === "M_ADJUSTMENT") {
      if (this.payment_type == "INTEREST") {
        this.getExtPayorList();
      }
      this.loadTPDropdownListForTaxAdj();
    }
    
    // Store original data for comparison
    this.entityOriginalData = JSON.parse(JSON.stringify(this.entityData));
    console.log("entityData set:", this.entityData);
  }

  /**
   * Load TP dropdown list for Tax Adjustments
   */
  loadTPDropdownListForTaxAdj() {
    let cur_tp_options: any[] = [];
    
    for (let i = 0; i < this.entityData.length; i++) {
      for (let j = 0; j < this.entityData[i].Account_data.length; j++) {
        for (let k = 0; k < this.entityData[i].Account_data[j].sub_acct_data.length; k++) {
          
          // Remove current LEID from TP dropdown list
          cur_tp_options = this.tp_pool_list.filter((b: any) => {
            return b.TP_LEID != this.entityData[i].LEID || 
                   b.TP_CDR_NO != this.entityData[i].CDR_NO || 
                   b.TP_REP_PD != this.entityData[i].REP_PD || 
                   b.TP_ME_CODE != this.entityData[i].ME_CODE;
          });
          
          if (this.payment_type == "INTEREST" && this.entityData[i].SOURCE_SYSTEM_FLAG == "D") {
            for (let t = 0; t < this.extPayorData.length; t++) {
              cur_tp_options.push({
                "TP_LEID": this.extPayorData[t].TP_LEID,
                "TP_CDR_NO": this.extPayorData[t].TP_CDR_NO,
                "TP_REP_PD": this.extPayorData[t].TP_REP_PD,
                "TP_ME_CODE": this.extPayorData[t].TP_ME_CODE,
                "TP_ENTITY": this.extPayorData[t].TP_LEID + "-" + 
                             this.extPayorData[t].TP_CDR_NO + "-" + 
                             this.extPayorData[t].TP_REP_PD + "-" + 
                             this.extPayorData[t].TP_ME_CODE
              });
            }
            
            cur_tp_options.push({
              "TP_LEID": "",
              "TP_CDR_NO": "",
              "TP_REP_PD": "",
              "TP_ME_CODE": "",
              "TP_ENTITY": "Unrelated Payor"
            });
          }
          
          console.log("::cur_tp_options after creation is :::", cur_tp_options);
          this.entityData[i].Account_data[j].sub_acct_data[k].tp_dropdown_data = cur_tp_options;
        }
      }
    }
  }

  /**
   * Add new payor row
   */
  addNewPayor(entity_index: number, acct_index: number, sub_index: number) {
    const new_row = JSON.parse(JSON.stringify(this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index]));
    
    console.log("::addNewPayor:::");
    console.log("::new_row:::", new_row);
    console.log("::entityData:::", this.entityData[entity_index]);
    
    // Remove current LEID|CDR|REP from TP dropdown list
    let cur_tp_options = this.tp_pool_list.filter((b: any) => {
      return b.TP_LEID != new_row.LEID || 
             b.TP_CDR_NO != new_row.CDR_NO || 
             b.TP_REP_PD != new_row.REPORTING_PERIOD || 
             b.TP_ME_CODE != new_row.ME_CODE;
    });
    
    console.log("::cur_tp_options after creation is :::", cur_tp_options);
    console.log("::extPayorData after creation is :::", this.extPayorData);
    
    if (this.payment_type == "INTEREST" && this.entityData[entity_index].SOURCE_SYSTEM_FLAG == "D") {
      for (let t = 0; t < this.extPayorData.length; t++) {
        cur_tp_options.push({
          "TP_LEID": this.extPayorData[t].TP_LEID,
          "TP_CDR_NO": this.extPayorData[t].TP_CDR_NO,
          "TP_REP_PD": this.extPayorData[t].TP_REP_PD,
          "TP_ME_CODE": this.extPayorData[t].TP_ME_CODE,
          "TP_ENTITY": this.extPayorData[t].TP_LEID + "-" + 
                       this.extPayorData[t].TP_CDR_NO + "-" + 
                       this.extPayorData[t].TP_REP_PD + "-" + 
                       this.extPayorData[t].TP_ME_CODE
        });
      }
    }
    
    console.log("::cur_tp_options after ext payor addition :::", cur_tp_options);
    
    // Set new row attributes & push to sub_acct array
    new_row.ROW_TYPE = "NEW";
    new_row.IS_CHANGED = 'Y';
    new_row.ALLOCATED_AMT = 0;
    new_row.tp_dropdown_data = cur_tp_options;
    new_row.TP_DETAILS = "";
    new_row.TP_LEID = "";
    new_row.TP_CDR_NO = "";
    new_row.TP_REP_PD = "";
    new_row.TP_ME_CODE = "";
    new_row.TP_ENTITY = "";
    
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data.push(new_row);
    
    // ✅ Setup autocomplete for the new row
    this.setupAutocompleteForRow(entity_index, acct_index, this.entityData[entity_index].Account_data[acct_index].sub_acct_data.length - 1, new_row.LINE);
    
    this.cdr.detectChanges();
  }

  /**
   * ✅ Setup autocomplete for a specific row (NEW rows only)
   * Creates Observable with debounce, distinctUntilChanged, and switchMap
   */
  setupAutocompleteForRow(mainIndex: number, acctIndex: number, subIndex: number, lineNo: string) {
    const key = `${mainIndex}_${acctIndex}_${subIndex}`;
    
    // Create a new Subject for this row if it doesn't exist
    if (!this.searchSubjects[key]) {
      this.searchSubjects[key] = new Subject<string>();
    }
    
    // Setup the Observable with debounce and switchMap
    this.filteredTradingPartners[key] = this.searchSubjects[key].pipe(
      debounceTime(300), // Wait 300ms after user stops typing (AngularJS uses 5ms but 300ms is better UX)
      distinctUntilChanged(), // Only emit if value is different from previous
      switchMap((searchValue: string) => {
        console.log(`🔍 Autocomplete search triggered for row ${key}, search: "${searchValue}"`);
        
        // Must have at least 3 characters (typeahead-min-length="3")
        if (!searchValue || searchValue.length < 3) {
          this.loadingTPs = false;
          this.noResults = false;
          return of([]);
        }
        
        this.loadingTPs = true;
        this.noResults = false;
        this.cdr.detectChanges();
        
        // Call the service method
        return this.service.searchTradingPartners(searchValue, lineNo, this.globalService.globalParams).pipe(
          catchError((error) => {
            console.error('Error in autocomplete search:', error);
            this.loadingTPs = false;
            this.noResults = true;
            this.cdr.detectChanges();
            return of([]);
          })
        );
      })
    );
    
    // Subscribe to handle the results
    this.filteredTradingPartners[key].pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results: any[]) => {
      this.loadingTPs = false;
      this.noResults = results.length === 0;
      console.log(`✅ Autocomplete results for row ${key}:`, results);
      this.cdr.detectChanges();
    });
  }

  /**
   * ✅ Handle autocomplete input change (triggered on every keystroke)
   */
  onAutocompleteInput(searchValue: string, mainIndex: number, acctIndex: number, subIndex: number) {
    const key = `${mainIndex}_${acctIndex}_${subIndex}`;
    
    // Push the search value to the Subject (will trigger debounced search)
    if (this.searchSubjects[key]) {
      this.searchSubjects[key].next(searchValue);
    }
  }

  /**
   * ✅ Handle autocomplete option selected
   */
  onAutocompleteSelected(option: any, mainIndex: number, acctIndex: number, subIndex: number) {
    console.log('🎯 Autocomplete option selected:', option);
    
    // Call the existing setTradingPartner method
    this.setTradingPartner(option, mainIndex, acctIndex, subIndex);
  }

  /**
   * Display function for autocomplete (shows TP_ENTITY or builds from parts)
   */
  displayTradingPartner(tp: any): string {
    if (!tp) return '';
    if (typeof tp === 'string') return tp;
    if (tp.TP_ENTITY) return tp.TP_ENTITY;
    // Build from parts if TP_ENTITY is empty
    if (tp.TP_LEID) {
      return tp.TP_LEID + '-' + tp.TP_CDR_NO + '-' + tp.TP_REP_PD + (tp.TP_ME_CODE ? '-' + tp.TP_ME_CODE : '');
    }
    return '';
  }

  /**
   * Remove payor row
   */
  removePayor(entity_index: number, acct_index: number, sub_index: number) {
    console.log("removePayor - indexes:", entity_index, acct_index, sub_index);
    console.log("entityData:", this.entityData);
    
    if (this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].ROW_TYPE == 'EX') {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_CHANGED = 'D';
      this.entityData[entity_index].Account_data[acct_index].DELETE_ROWS.push(
        this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index]
      );
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data.splice(sub_index, 1);
    } else if (this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].ROW_TYPE == 'NEW') {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data.splice(sub_index, 1);
    }
    
    this.updateAmount(entity_index, acct_index, sub_index, 'R');
    this.cdr.detectChanges();
  }

  /**
   * Update allocated/unallocated amounts
   */
  updateAmount(entity_index: number, acct_index: number, sub_index: number, flag: string) {
    console.log("updateAmount - entityData:", this.entityData);
    
    let allocated_amt = 0;
    for (let k = 1; k < this.entityData[entity_index].Account_data[acct_index].sub_acct_data.length; k++) {
      if (this.entityData[entity_index].Account_data[acct_index].sub_acct_data[k].TP_LEID != null && 
          this.entityData[entity_index].Account_data[acct_index].sub_acct_data[k].TP_LEID != undefined) {
        allocated_amt = allocated_amt + (parseFloat(this.entityData[entity_index].Account_data[acct_index].sub_acct_data[k].ALLOCATED_AMT) || 0);
      }
    }
    
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[0].TOTAL_ALLOCATED_AMT = allocated_amt;
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[0].UNALLOCATED_AMT = 
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[0].BOOK_AMT - 
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[0].TOTAL_ALLOCATED_AMT;
    
    this.cdr.detectChanges();
  }

  /**
   * Get trading partners with typeahead search
   */
  getTradingPartners(line: string, searchValue: string): Promise<any[]> {
    if (!searchValue || searchValue.length < 3) {
      return Promise.resolve([]);
    }
    
    this.loadingTPs = true;
    this.cdr.detectChanges();
    
    return new Promise((resolve, reject) => {
      this.service.searchTradingPartners(searchValue, line, this.globalService.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (results: any[]) => {
          this.loadingTPs = false;
          this.cdr.detectChanges();
          resolve(results || []);
        },
        (error: any) => {
          this.loadingTPs = false;
          this.cdr.detectChanges();
          reject(error);
        }
      );
    });
  }

  /**
   * Set trading partner selection
   */
  setTradingPartner(tp_details: any, entity_index: number, acct_index: number, sub_index: number) {
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].HAS_ERROR = 'N';
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_CHANGED = 'Y';
    
    if (tp_details.IS_VALID == 1 || tp_details.TP_ENTITY != "Unrelated Payor") {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_OTHER_PAYOR = 'N';
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_LEID = tp_details.TP_LEID;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_CDR_NO = tp_details.TP_CDR_NO;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_REP_PD = tp_details.TP_REP_PD;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_ME_CODE = tp_details.TP_ME_CODE;
      // In AngularJS, the typeahead ng-model overwrites TP_DETAILS AFTER this callback.
      // Angular Material autocomplete sets ngModel BEFORE optionSelected event.
      // So we keep TP_DETAILS as the full autocomplete-selected object (with TP_ENTITY, IS_VALID, etc.)
      // Do NOT build a new stripped-down object here.
      console.log("entityData after setTradingPartner:", this.entityData);
    } else if (tp_details.IS_VALID == 0) {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_OTHER_PAYOR = 'Y';
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_LEID = tp_details.TP_LEID;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_CDR_NO = tp_details.TP_CDR_NO;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_REP_PD = tp_details.TP_REP_PD;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_ME_CODE = tp_details.TP_ME_CODE;
    }
    
    if (tp_details.TP_ENTITY == "Unrelated Payor") {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_OTHER_PAYOR = 'Y';
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_LEID = tp_details.TP_LEID;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_CDR_NO = tp_details.TP_CDR_NO;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_REP_PD = tp_details.TP_REP_PD;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_ME_CODE = tp_details.TP_ME_CODE;
      console.log("entityData after unrelated payor:", this.entityData);
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Set payor type
   */
  setPayorType(payor_type: string, entity_index: number, acct_index: number, sub_index: number) {
    console.log("payor_type selected is:", payor_type);
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_CHANGED = 'Y';
    
    if (payor_type != null && payor_type != undefined) {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].HAS_TYPE_ERROR = 'N';
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].PAYOR_TYPE = payor_type;
    }
    
    console.log("entityData after setPayorType:", this.entityData);
    this.cdr.detectChanges();
  }

  /**
   * Update payor details
   */
  updatingPayor(entity_index: number, acct_index: number, sub_index: number, tp_details: any) {
    console.log("updatingPayor the indexes are:", entity_index, acct_index, sub_index);
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].HAS_ERROR = 'N';
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_CHANGED = 'Y';
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_OTHER_PAYOR = 'N';
    
    if (tp_details.TP_ENTITY == "Unrelated Payor") {
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_OTHER_PAYOR = 'Y';
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_LEID = tp_details.TP_LEID;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_CDR_NO = tp_details.TP_CDR_NO;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_REP_PD = tp_details.TP_REP_PD;
      this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].TP_ME_CODE = tp_details.TP_ME_CODE;
      console.log("entityData after updatingPayor:", this.entityData);
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Updated details (mark as changed)
   */
  updatedDetails(entity_index: number, acct_index: number, sub_index: number) {
    this.entityData[entity_index].Account_data[acct_index].sub_acct_data[sub_index].IS_CHANGED = 'Y';
    this.cdr.detectChanges();
  }

  /**
   * Set TP ME List
   */
  setTPMEList(tp: any, entity_index: number, acctIndex: number, index: number) {
    let tp_me_dropdown_data: any[] = [];
    
    for (let i = 0; i < this.entityData.length; i++) {
      if (this.entityData[i].LEID === tp.TP_LEID) {
        tp_me_dropdown_data.push({"TP_ME_CODE": this.entityData[i].ME_CODE});
      }
    }
    
    this.entityData[entity_index].Account_data[acctIndex].sub_acct_data[index].tp_me_dropdown_data = tp_me_dropdown_data;
    
    console.log(":::::setTPMEList::::::");
    console.log(":::::entity data::::::", this.entityData);
    console.log(":::::entity_index::::::", entity_index);
    console.log(":::::acct_index::::::", acctIndex);
    console.log(":::::subacct_index::::::", index);
    console.log(":::::tp obj:::::", tp);
    
    this.cdr.detectChanges();
  }

  /**
   * Validate form values before save
   */
  validateFormValues(): number {
    let returnValue = 0;
    
    for (let i = 0; i < this.entityData.length; i++) {
      if (this.entityData[i].Account_data.length != null && this.entityData[i].Account_data.length != undefined) {
        for (let j = 0; j < this.entityData[i].Account_data.length; j++) {
          if (this.entityData[i].Account_data[j].sub_acct_data.length != null && 
              this.entityData[i].Account_data[j].sub_acct_data.length != undefined) {
            for (let k = 0; k < this.entityData[i].Account_data[j].sub_acct_data.length; k++) {
              
              if (this.trans_type === "BOOK_AMT") {
                if (this.entityData[i].Account_data[j].sub_acct_data[k].ROW_TYPE == 'NEW') {
                  if (this.entityData[i].Account_data[j].sub_acct_data[k].hasOwnProperty('IS_OTHER_PAYOR') && 
                      this.entityData[i].Account_data[j].sub_acct_data[k].IS_OTHER_PAYOR == 'Y') {
                    if (this.entityData[i].Account_data[j].sub_acct_data[k].PAYOR_TYPE == null) {
                      this.entityData[i].Account_data[j].sub_acct_data[k].HAS_TYPE_ERROR = 'Y';
                      this.entityData[i].Account_data[j].sub_acct_data[k].PAYOR_TYPE_ERROR = 'Please select the Payor Type';
                      returnValue = 1;
                    }
                  } else if (!this.entityData[i].Account_data[j].sub_acct_data[k].hasOwnProperty('TP_DETAILS') || 
                             this.entityData[i].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_LEID == undefined || 
                             this.entityData[i].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_LEID == null) {
                    this.entityData[i].Account_data[j].sub_acct_data[k].HAS_ERROR = 'Y';
                    this.entityData[i].Account_data[j].sub_acct_data[k].TP_ERROR = 'Please assign the Payor';
                    returnValue = 1;
                  }
                }
              } else {
                if (this.payment_type === "M3PAYOR" && 
                    this.entityData[i].Account_data[j].sub_acct_data[k].hasOwnProperty('IS_OTHER_PAYOR') && 
                    this.entityData[i].Account_data[j].sub_acct_data[k].IS_OTHER_PAYOR == 'Y') {
                  if (this.entityData[i].Account_data[j].sub_acct_data[k].PAYOR_TYPE == null) {
                    this.entityData[i].Account_data[j].sub_acct_data[k].HAS_TYPE_ERROR = 'Y';
                    this.entityData[i].Account_data[j].sub_acct_data[k].PAYOR_TYPE_ERROR = 'Please select the Payor Type';
                    returnValue = 1;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return returnValue;
  }

  /**
   * Allocate payor process (for Interest payment type)
   */
  allocate_payor(param: string) {
    const message = "Allocation process was success";
    
    // Group by CC_KEY
    const grouped: any = {};
    this.payorAccountData.forEach((item: any) => {
      if (!grouped[item.CC_KEY]) {
        grouped[item.CC_KEY] = [];
      }
      grouped[item.CC_KEY].push(item);
    });
    
    console.log("object grouped:", grouped);
    
    let allocateprocessdata: any[] = [];
    
    if (param == 'A') {
      allocateprocessdata = [{
        CC_KEY: null, 
        leid: null, 
        cdr_number: null, 
        reporting_period: null, 
        me_code: null, 
        LEDGER: null
      }];
    } else {
      allocateprocessdata = Object.keys(grouped).map((k: string) => {
        const items = grouped[k];
        return {
          CC_KEY: items[0].CC_KEY,
          leid: items[0].LEID,
          cdr_number: items[0].CDR_NO,
          reporting_period: items[0].REPORTING_PERIOD,
          me_code: items[0].ME_CODE,
          LEDGER: items[0].LEDGER
        };
      });
    }
    
    console.log("object allocateprocessdata:", allocateprocessdata);
    
    this.clob_settings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalService.globalParams?.screen_key
    }];
    
    this.crudLoading = true;
    this.cdr.detectChanges();
    
    this.service.allocateProcess(allocateprocessdata, this.clob_settings, this.globalService.globalParams, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("Result from allocate process:", data);
        this.crudLoading = false;
        
        if (data.errorMessage && data.errorMessage != 'null') {
          console.log("There was the error message:", data.errorMessage);
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          console.log("Success - no error message");
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error in allocate process:", error);
        this.crudLoading = false;
        this._snackBar.open('Error occurred during allocation process', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Save payor modal data
   */
  save() {
    // Prevent double-click
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }
    
    let finalData: any[] = [];
    this.clob_settings = [];
    
    console.log("Original Data:", this.entityOriginalData);
    console.log("entityData:", this.entityData);
    
    const entityFinalData = JSON.parse(JSON.stringify(this.entityData));
    
    // Check if data changed
    if (JSON.stringify(entityFinalData) === JSON.stringify(this.entityOriginalData)) {
      this.cancel();
      return;
    }
    
    // Validate form
    const returnValue = this.validateFormValues();
    if (returnValue == 1) {
      this.cdr.detectChanges();
      return;
    }
    
    // Process data for save
    for (let x = 0; x < entityFinalData.length; x++) {
      if (entityFinalData[x].Account_data.length != null && entityFinalData[x].Account_data.length != undefined) {
        for (let j = 0; j < entityFinalData[x].Account_data.length; j++) {
          
          if (this.trans_type === "BOOK_AMT") {
            entityFinalData[x].Account_data[j].sub_acct_data.splice(0, 1);
          }
          
          if (entityFinalData[x].Account_data[j].sub_acct_data.length != null && 
              entityFinalData[x].Account_data[j].sub_acct_data.length != undefined) {
            for (let k = 0; k < entityFinalData[x].Account_data[j].sub_acct_data.length; k++) {
              
              if (entityFinalData[x].Account_data[j].sub_acct_data[k].IS_CHANGED === 'Y' && 
                  entityFinalData[x].Account_data[j].sub_acct_data[k].IS_OTHER_PAYOR == 'N') {
                
                if (this.classification_type !== "M3PAYOR") {
                  entityFinalData[x].Account_data[j].sub_acct_data[k].TP_LEID = 
                    entityFinalData[x].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_LEID;
                  entityFinalData[x].Account_data[j].sub_acct_data[k].TP_CDR_NO = 
                    entityFinalData[x].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_CDR_NO;
                  entityFinalData[x].Account_data[j].sub_acct_data[k].TP_REP_PD = 
                    entityFinalData[x].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_REP_PD;
                  entityFinalData[x].Account_data[j].sub_acct_data[k].TP_ME_CODE = 
                    entityFinalData[x].Account_data[j].sub_acct_data[k].TP_DETAILS.TP_ME_CODE;
                }
                
                finalData.push(entityFinalData[x].Account_data[j].sub_acct_data[k]);
              } else {
                finalData.push(entityFinalData[x].Account_data[j].sub_acct_data[k]);
              }
            }
          }
          
          // Process deleted rows
          if (entityFinalData[x].Account_data[j].DELETE_ROWS.length != null && 
              entityFinalData[x].Account_data[j].DELETE_ROWS.length != undefined) {
            for (let k = 0; k < entityFinalData[x].Account_data[j].DELETE_ROWS.length; k++) {
              finalData.push(entityFinalData[x].Account_data[j].DELETE_ROWS[k]);
            }
          }
        }
      }
    }
    
    // Ensure ALLOCATED_AMT is always a number (input fields may produce strings)
    for (let i = 0; i < finalData.length; i++) {
      if (finalData[i].ALLOCATED_AMT !== undefined && finalData[i].ALLOCATED_AMT !== null) {
        finalData[i].ALLOCATED_AMT = parseFloat(finalData[i].ALLOCATED_AMT) || 0;
      }
      // Remove TP_LOAD if it exists (not part of the AngularJS payload)
      if (finalData[i].hasOwnProperty('TP_LOAD')) {
        delete finalData[i].TP_LOAD;
      }
    }
    
    console.log("After changes, the final data was:", entityFinalData);
    console.log("After changes, the manipulated data was:", this.entityData);
    console.log("After changes, the final data to send DB was:", finalData);
    console.log("Data for the first time from DB was:", this.payorAccountData);
    
    this.clob_settings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalService.globalParams?.screen_key
    }];
    
    this.sendDetailsDB(finalData, this.clob_settings);
  }

  /**
   * Send details to database
   */
  sendDetailsDB(final_data: any[], clob_settings: any[]) {
    const message = "Related Party Modal Data was Saved";
    
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();
    
    this.service.saveRPModal(
      final_data,
      clob_settings,
      this.classification_type,
      this.trans_type,
      this.globalService.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log("Result from save:", data);
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (data.errorMessage && data.errorMessage != 'null') {
          console.log("There was the error message:", data.errorMessage);
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          console.log("There was no error message");
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error("Error saving data:", error);
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Reset form to original data
   */
  reset() {
    this.entityData = JSON.parse(JSON.stringify(this.entityOriginalData));
    this.cdr.detectChanges();
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    // Check if data changed
    if (JSON.stringify(this.entityData) === JSON.stringify(this.entityOriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open('The transaction is still in progress', 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
      } else if (confirm("Do you want to close Form without saving changes?")) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this._snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this._snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Show info message
   */
  private showInfo(message: string): void {
    this._snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['info-snackbar']
    });
  }
}
